/**
 * Hook TanStack Query per il dominio Product/Release/BOM.
 * Ogni hook incapsula la query key, la funzione di fetch e (per le mutation)
 * l'invalidazione delle query correlate dopo il successo.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  fetchProducts,
  fetchProduct,
  createProduct,
  deleteProduct,
  fetchReleases,
  fetchReleaseComparison,
  createRelease,
  fetchArchivedReleases,
  setReleaseArchived,
  fetchMappings,
  addMapping,
  removeMapping,
  importMappingsCsv,
  fetchSbomContent,
  fetchBundles,
  fetchOpenVulnBundles,
  fetchPendingScanBundles,
  fetchOwnership,
  addOwnership,
  removeOwnership,
  fetchDeployments,
  createDeployment,
  deleteDeployment,
  bulkDeliver,
  fetchImpact,
  notifyCustomer,
  notifyAllImpacted,
  fetchNotifications,
  fetchComponents,
  createComponent,
  attachComponentBom,
  detachComponentBom,
  fetchBomArtifacts,
} from './api.js';
import type {
  ActiveBundle,
  BomArtifact,
  BomArtifactScope,
  BulkDeliveryInput,
  ComponentBomAttachInput,
  ComponentBomWithArtifact,
  CreateComponentInput,
  CreateDeploymentInput,
  CreateProductInput,
  CreateReleaseInput,
  CsvImportSummary,
  CustomerVulnNotification,
  Deployment,
  IdentifierMapping,
  ImpactResponse,
  MappingInput,
  NotifyAllInput,
  NotifyAllResponse,
  NotifyInput,
  OpenVulnSets,
  PendingScanSets,
  OwnershipInput,
  Product,
  ProductOwnership,
  Release,
  ReleaseComponent,
} from './types.js';
import type { CycloneDxBom } from './sbomDiff.js';
import type { ReleaseComparison } from './releaseCompare.js';
import { useSrpReports, useSlaTimers } from '../srp/hooks.js';
import { computeProductNextSla, type ProductNextSla } from './productNextSla.js';

// ---------------------------------------------------------------------------
// Chiavi query centralizzate
// ---------------------------------------------------------------------------

export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  detail: (id: string) => [...productKeys.all, 'detail', id] as const,
  releases: (id: string) => [...productKeys.all, 'releases', id] as const,
  archivedReleases: (id: string) => [...productKeys.releases(id), 'archived'] as const,
  mappings: (id: string) => [...productKeys.all, 'mappings', id] as const,
  sbomContent: (id: string, version: string, componentId: number) =>
    [...productKeys.all, 'sbom-content', id, version, componentId] as const,
  bundles: () => [...productKeys.all, 'bundles'] as const,
  openVulns: () => [...productKeys.all, 'open-vulns'] as const,
  pendingScan: () => [...productKeys.all, 'pending-scan'] as const,
  ownership: (id: string) => [...productKeys.all, 'ownership', id] as const,
  deployments: (id: string, currentOnly: boolean) =>
    [...productKeys.all, 'deployments', id, currentOnly] as const,
  impact: (id: string, version: string) => [...productKeys.all, 'impact', id, version] as const,
  notifications: (id: string, version: string) =>
    [...productKeys.all, 'notifications', id, version] as const,
  components: (id: string, version: string) =>
    [...productKeys.all, 'components', id, version] as const,
  releaseCompare: (id: string, from: string, to: string) =>
    [...productKeys.all, 'release-compare', id, from, to] as const,
  releaseAudit: (id: string) => [...productKeys.all, 'release-audit', id] as const,
  bomArtifacts: (scope: BomArtifactScope, productId?: string, q?: string) =>
    [...productKeys.all, 'bom-artifacts', scope, productId, q] as const,
} as const;

// ---------------------------------------------------------------------------
// Prodotti
// ---------------------------------------------------------------------------

/** Recupera la lista di tutti i prodotti. */
export function useProducts(): UseQueryResult<Product[]> {
  return useQuery({
    queryKey: productKeys.lists(),
    queryFn: fetchProducts,
  });
}

/**
 * Recupera TUTTI i bundle attivi (foglie DT) col linkage Release/Product.
 * Usato per risolvere il breadcrumb di una foglia nel triage (nome prodotto/release/bundle).
 */
export function useBundles(): UseQueryResult<ActiveBundle[]> {
  return useQuery({
    queryKey: productKeys.bundles(),
    queryFn: fetchBundles,
  });
}

/** Recupera un singolo prodotto per ID interno. */
export function useProduct(id: string): UseQueryResult<Product> {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: () => fetchProduct(id),
    enabled: id.length > 0,
  });
}

/** Crea un nuovo prodotto e invalida la lista. */
export function useCreateProduct(): UseMutationResult<Product, Error, CreateProductInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
}

/** Soft-delete di un prodotto e invalida lista + dettaglio. */
export function useDeleteProduct(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: productKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: productKeys.detail(id) });
    },
  });
}

// ---------------------------------------------------------------------------
// Release
// ---------------------------------------------------------------------------

/** Recupera le release di un prodotto. */
export function useReleases(productId: string): UseQueryResult<Release[]> {
  return useQuery({
    queryKey: productKeys.releases(productId),
    queryFn: () => fetchReleases(productId),
    enabled: productId.length > 0,
  });
}

/** Dopo una modifica a release/bundle/BOM: storico release e confronti vanno riletti. */
function invalidaStoricoRelease(queryClient: QueryClient, productId: string): void {
  void queryClient.invalidateQueries({ queryKey: productKeys.releaseAudit(productId) });
  void queryClient.invalidateQueries({
    queryKey: [...productKeys.all, 'release-compare', productId],
  });
}

/** Confronto fra due release: abilitato solo con due versioni diverse selezionate. */
export function useReleaseComparison(
  productId: string,
  from: string,
  to: string,
): UseQueryResult<ReleaseComparison> {
  return useQuery({
    queryKey: productKeys.releaseCompare(productId, from, to),
    queryFn: () => fetchReleaseComparison(productId, from, to),
    enabled: productId.length > 0 && from !== '' && to !== '' && from !== to,
  });
}

/** Crea una release e invalida le release del prodotto. */
export function useCreateRelease(
  productId: string,
): UseMutationResult<Release, Error, CreateReleaseInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => createRelease(productId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.releases(productId) });
      invalidaStoricoRelease(queryClient, productId);
    },
  });
}

/** Release archiviate del prodotto: lette solo quando la vista "Archiviate" è aperta. */
export function useArchivedReleases(
  productId: string,
  enabled: boolean,
): UseQueryResult<Release[]> {
  return useQuery({
    queryKey: productKeys.archivedReleases(productId),
    queryFn: () => fetchArchivedReleases(productId),
    enabled: enabled && productId.length > 0,
  });
}

/**
 * Archivia (`archived: true`) o ripristina una release. Invalida liste release (attive e
 * archiviate), storico e bubble-up vulnerabilità/scansioni: le archiviate escono dai flow.
 */
export function useSetReleaseArchived(
  productId: string,
): UseMutationResult<Release, Error, { version: string; archived: boolean }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ version, archived }) => setReleaseArchived(productId, version, archived),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.releases(productId) });
      void queryClient.invalidateQueries({ queryKey: productKeys.openVulns() });
      void queryClient.invalidateQueries({ queryKey: productKeys.pendingScan() });
      void queryClient.invalidateQueries({ queryKey: productKeys.bundles() });
      invalidaStoricoRelease(queryClient, productId);
    },
  });
}

// ---------------------------------------------------------------------------
// Mapping identificatori
// ---------------------------------------------------------------------------

/** Recupera i mapping di un prodotto. */
export function useMappings(productId: string): UseQueryResult<IdentifierMapping[]> {
  return useQuery({
    queryKey: productKeys.mappings(productId),
    queryFn: () => fetchMappings(productId),
    enabled: productId.length > 0,
  });
}

/** Aggiunge un mapping e invalida la lista mapping del prodotto. */
export function useAddMapping(
  productId: string,
): UseMutationResult<IdentifierMapping, Error, MappingInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => addMapping(productId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.mappings(productId) });
    },
  });
}

/** Rimuove un mapping e invalida la lista mapping del prodotto. */
export function useRemoveMapping(productId: string): UseMutationResult<void, Error, MappingInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => removeMapping(productId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.mappings(productId) });
    },
  });
}

/** Import CSV mapping con supporto dry-run. */
export function useImportMappings(): UseMutationResult<
  CsvImportSummary,
  Error,
  { csv: string; dryRun: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ csv, dryRun }) => importMappingsCsv(csv, dryRun),
    onSuccess: (_data, { dryRun }) => {
      // Invalida i mapping solo se è stato un import reale (non dry-run)
      if (!dryRun) {
        void queryClient.invalidateQueries({ queryKey: productKeys.all });
      }
    },
  });
}

// ---------------------------------------------------------------------------
// SBOM (per-bundle: la foglia DT — Slice 7d)
// ---------------------------------------------------------------------------

/**
 * Recupera il contenuto CycloneDX del SBOM di un BUNDLE (foglia DT) per viewer/diff.
 * `enabled` permette il fetch lazy (solo quando il pannello è aperto).
 * retry:false → il 404 (bundle senza SBOM/Project DT) è uno stato valido, non un errore.
 */
export function useSbomContent(
  productId: string,
  version: string,
  componentId: number,
  enabled = true,
): UseQueryResult<CycloneDxBom> {
  return useQuery({
    queryKey: productKeys.sbomContent(productId, version, componentId),
    queryFn: () => fetchSbomContent(productId, version, componentId),
    enabled: enabled && productId.length > 0 && version.length > 0 && componentId > 0,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Bubble-up vulnerabilità aperte (Slice 8c)
// ---------------------------------------------------------------------------

/**
 * Set derivati dai bundle con CVE aperte (GET /bundles-with-open-cves): Product/Release/Bundle
 * affetti, per badge + ordinamento. `select` deriva i Set una volta sola per dataset.
 */
export function useOpenVulnSets(): UseQueryResult<OpenVulnSets> {
  return useQuery({
    queryKey: productKeys.openVulns(),
    queryFn: fetchOpenVulnBundles,
    staleTime: 60_000,
    select: (bundles): OpenVulnSets => {
      // Finding aperti per release (somma sui bundle della release) + finding aperti per Product
      // (somma sui bundle del prodotto, = numero di vulnerabilità da gestire).
      const openCountByRelease = new Map<string, number>();
      const openCountByProduct = new Map<string, number>();
      for (const b of bundles) {
        openCountByRelease.set(
          b.releaseProjectUuid,
          (openCountByRelease.get(b.releaseProjectUuid) ?? 0) + b.openVulnCount,
        );
        openCountByProduct.set(
          b.productInternalId,
          (openCountByProduct.get(b.productInternalId) ?? 0) + b.openVulnCount,
        );
      }
      return {
        productIds: new Set(bundles.map((b) => b.productInternalId)),
        releaseUuids: new Set(bundles.map((b) => b.releaseProjectUuid)),
        bundleUuids: new Set(bundles.map((b) => b.bundleProjectUuid)),
        openVulnCountByProduct: openCountByProduct,
        openCountByBundle: new Map(bundles.map((b) => [b.bundleProjectUuid, b.openVulnCount])),
        openCountByRelease,
      };
    },
  });
}

/**
 * Set derivati dai bundle "scansione in sospeso" (GET /bundles-pending-scan): Product/Release/Bundle
 * pending per il badge che bolla a tutti i livelli. `select` deriva i Set una volta per dataset.
 */
export function usePendingScanSets(): UseQueryResult<PendingScanSets> {
  return useQuery({
    queryKey: productKeys.pendingScan(),
    queryFn: fetchPendingScanBundles,
    staleTime: 60_000,
    select: (bundles): PendingScanSets => ({
      productIds: new Set(bundles.map((b) => b.productInternalId)),
      releaseUuids: new Set(bundles.map((b) => b.releaseProjectUuid)),
      bundleUuids: new Set(bundles.map((b) => b.bundleProjectUuid)),
    }),
  });
}

// ---------------------------------------------------------------------------
// Ownership / destinatari notifiche (E14.3)
// ---------------------------------------------------------------------------

/** Recupera i destinatari notifiche (ownership) configurati per un prodotto. */
export function useOwnership(productId: string): UseQueryResult<ProductOwnership[]> {
  return useQuery({
    queryKey: productKeys.ownership(productId),
    queryFn: () => fetchOwnership(productId),
    enabled: productId.length > 0,
  });
}

/** Aggiunge un destinatario e invalida la lista ownership del prodotto. */
export function useAddOwnership(
  productId: string,
): UseMutationResult<ProductOwnership, Error, OwnershipInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => addOwnership(productId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.ownership(productId) });
    },
  });
}

/** Rimuove un destinatario e invalida la lista ownership del prodotto. */
export function useRemoveOwnership(productId: string): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId) => removeOwnership(productId, entryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.ownership(productId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Forniture / Deployments (Slice B)
// ---------------------------------------------------------------------------

/** Recupera le consegne di un prodotto; currentOnly=true per solo le righe correnti. */
export function useDeployments(
  productId: string,
  currentOnly = false,
): UseQueryResult<Deployment[]> {
  return useQuery({
    queryKey: productKeys.deployments(productId, currentOnly),
    queryFn: () => fetchDeployments(productId, currentOnly),
    enabled: productId.length > 0,
  });
}

/** Crea una consegna e invalida la lista deployments del prodotto. */
export function useCreateDeployment(
  productId: string,
): UseMutationResult<Deployment, Error, CreateDeploymentInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => createDeployment(productId, input),
    onSuccess: () => {
      // Invalida entrambe le viste (tutte e solo correnti)
      void queryClient.invalidateQueries({
        queryKey: productKeys.deployments(productId, false),
      });
      void queryClient.invalidateQueries({
        queryKey: productKeys.deployments(productId, true),
      });
    },
  });
}

/** Soft-delete di una consegna e invalida la lista deployments del prodotto. */
export function useDeleteDeployment(productId: string): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (depId) => deleteDeployment(productId, depId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: productKeys.deployments(productId, false),
      });
      void queryClient.invalidateQueries({
        queryKey: productKeys.deployments(productId, true),
      });
    },
  });
}

/** Push massivo di consegne (all-previous) e invalida la lista deployments. */
export function useBulkDeliver(
  productId: string,
  version: string,
): UseMutationResult<Deployment[], Error, BulkDeliveryInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => bulkDeliver(productId, version, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: productKeys.deployments(productId, false),
      });
      void queryClient.invalidateQueries({
        queryKey: productKeys.deployments(productId, true),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Impatto + notifiche manuali (Slice C)
// ---------------------------------------------------------------------------

/** Recupera i clienti impattati dalla release (consegna corrente) + storico ledger. */
export function useImpact(productId: string, version: string): UseQueryResult<ImpactResponse> {
  return useQuery({
    queryKey: productKeys.impact(productId, version),
    queryFn: () => fetchImpact(productId, version),
    enabled: productId.length > 0 && version.length > 0,
  });
}

/** Recupera lo storico invii (ledger) per (product, release). */
export function useNotifications(
  productId: string,
  version: string,
): UseQueryResult<CustomerVulnNotification[]> {
  return useQuery({
    queryKey: productKeys.notifications(productId, version),
    queryFn: () => fetchNotifications(productId, version),
    enabled: productId.length > 0 && version.length > 0,
  });
}

/**
 * Notifica manualmente un cliente impattato per un finding. Invalida impatto e
 * storico invii al termine (anche in caso di 502 — la riga ledger e' comunque
 * registrata, ma in quel caso la mutation va in errore e l'invalidazione avviene
 * solo su successo: il chiamante puo' comunque re-fetchare manualmente).
 */
export function useNotifyCustomer(
  productId: string,
  version: string,
): UseMutationResult<CustomerVulnNotification, Error, { vulnId: string; input: NotifyInput }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ vulnId, input }) => notifyCustomer(productId, version, vulnId, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.impact(productId, version) });
      void queryClient.invalidateQueries({
        queryKey: productKeys.notifications(productId, version),
      });
    },
  });
}

/** Notifica manualmente tutti i clienti impattati per un finding. */
export function useNotifyAllImpacted(
  productId: string,
  version: string,
): UseMutationResult<NotifyAllResponse, Error, { vulnId: string; input: NotifyAllInput }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ vulnId, input }) => notifyAllImpacted(productId, version, vulnId, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.impact(productId, version) });
      void queryClient.invalidateQueries({
        queryKey: productKeys.notifications(productId, version),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Componenti Release + libreria BOM (Slice 4/5)
// ---------------------------------------------------------------------------

/** Recupera i componenti di una release con i relativi BOM. */
export function useComponents(
  productId: string,
  version: string,
): UseQueryResult<ReleaseComponent[]> {
  return useQuery({
    queryKey: productKeys.components(productId, version),
    queryFn: () => fetchComponents(productId, version),
    enabled: productId.length > 0 && version.length > 0,
  });
}

/** Crea un componente per una release e invalida i componenti della release. */
export function useCreateComponent(
  productId: string,
  version: string,
): UseMutationResult<ReleaseComponent, Error, CreateComponentInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => createComponent(productId, version, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.components(productId, version) });
      invalidaStoricoRelease(queryClient, productId);
    },
  });
}

/** Attacca un BOM (artefatto esistente o nuovo upload) a un componente. */
export function useAttachComponentBom(
  productId: string,
  version: string,
): UseMutationResult<
  ComponentBomWithArtifact,
  Error,
  { componentId: number; input: ComponentBomAttachInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ componentId, input }) =>
      attachComponentBom(productId, version, componentId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.components(productId, version) });
      invalidaStoricoRelease(queryClient, productId);
      void queryClient.invalidateQueries({ queryKey: productKeys.bomArtifacts('product') });
      void queryClient.invalidateQueries({ queryKey: productKeys.bomArtifacts('mine') });
      void queryClient.invalidateQueries({ queryKey: productKeys.bomArtifacts('all') });
      // Attaccare un BOM modifica il bundle → diventa "in attesa di scansione":
      // rinfresca i set pending-scan che alimentano il badge (senza reload manuale).
      void queryClient.invalidateQueries({ queryKey: productKeys.pendingScan() });
    },
  });
}

/** Stacca (soft-delete) un BOM da un componente e invalida i componenti della release. */
export function useDetachComponentBom(
  productId: string,
  version: string,
): UseMutationResult<void, Error, { componentId: number; membershipId: number }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ componentId, membershipId }) =>
      detachComponentBom(productId, version, componentId, membershipId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.components(productId, version) });
      invalidaStoricoRelease(queryClient, productId);
      // Anche staccare un BOM modifica il bundle → aggiorna il badge pending-scan.
      void queryClient.invalidateQueries({ queryKey: productKeys.pendingScan() });
    },
  });
}

/**
 * Recupera la libreria artefatti BOM (picker). scope='product' → artefatti usati dal
 * prodotto; scope='mine' → artefatti dell'utente corrente (uploadedAt DESC). `q` filtra
 * per filename.
 */
export function useBomArtifacts(params: {
  scope: BomArtifactScope;
  productId?: string;
  q?: string;
  enabled?: boolean;
}): UseQueryResult<BomArtifact[]> {
  const { scope, productId, q, enabled = true } = params;
  return useQuery({
    queryKey: productKeys.bomArtifacts(scope, productId, q),
    queryFn: () => fetchBomArtifacts({ scope, productId, q }),
    enabled: enabled && (scope === 'mine' || scope === 'all' || !!productId),
  });
}

// ---------------------------------------------------------------------------
// Prossima scadenza SLA per prodotto (colonna lista prodotti)
// ---------------------------------------------------------------------------

/**
 * Restituisce `Date.now()` aggiornato a intervalli regolari (countdown live).
 * Forza il recompute del remaining SLA senza dover rifetchare i report.
 */
function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * Mappa productInternalId → prossima scadenza SLA (ore rimanenti + fase), derivata dai
 * report SRP aperti (GET /srp/reports) linkati ai prodotti via bundle attivi (GET /bundles).
 * Ritorna `undefined` finché entrambe le query non sono risolte (stato di caricamento).
 * Il countdown si aggiorna ogni 60s via `useNow` (senza rifetchare i report).
 */
export function useProductNextSlaMap(): Map<string, ProductNextSla> | undefined {
  const { data: reports } = useSrpReports();
  const { data: bundles } = useBundles();
  const { data: timers } = useSlaTimers();
  const now = useNow(60_000);

  return useMemo(() => {
    if (!reports || !bundles) return undefined;
    // Mappa releaseProjectUuid → productInternalId dai bundle attivi (qualsiasi bundle della
    // release basta a linkare il report SRP del release stesso al prodotto).
    const releaseToProduct = new Map<string, string>();
    for (const b of bundles) {
      if (
        b.releaseProjectUuid &&
        b.productInternalId &&
        !releaseToProduct.has(b.releaseProjectUuid)
      ) {
        releaseToProduct.set(b.releaseProjectUuid, b.productInternalId);
      }
    }
    return computeProductNextSla(reports, releaseToProduct, now, timers);
  }, [reports, bundles, timers, now]);
}
